import * as babylon from "@babylonjs/core";

import type { OrbitTargetKind } from "@/shared/state/visualization/schema/v1/three-d";

import { BabylonState } from "../../state";

/**
 * Reused across the whole pass, so a 60fps loop over every entity allocates
 * nothing. Safe as module state because this runs to completion synchronously on
 * one thread, and neither value outlives a single iteration.
 */
const SCRATCH_OFFSET = babylon.Vector3.Zero();
const IDENTITY = babylon.Quaternion.Identity();

/**
 * The absolute data-space point a camera target names, or `undefined` if it
 * cannot be resolved yet.
 *
 * A `mesh` or `pointCloud` target follows the entity's live pose; a `path`
 * target follows the mean of its trajectory, so the camera frames the whole
 * trace rather than chasing the object along it. For a cloud this is the
 * sensor, and `targetFocus` adds the offset to the cloud's middle.
 *
 * Shared by every consumer that needs a tracked entity's position — orbit
 * follow, recenter framing, and initial camera construction — so they cannot
 * drift apart on which vector means what.
 *
 * An orbit camera may follow one node *inside* the model an entity draws rather
 * than the entity itself, or the middle of the cloud it draws; `targetFocus`
 * takes this as its position term and adds that offset. This function is
 * unaffected by that and always means the entity's own transform.
 */
export function targetPosition(
  state: BabylonState,
  target: { id: string; mesh: OrbitTargetKind },
): babylon.Vector3 | undefined {
  const global = state.entities[target.id]?.globalPosition;
  if (!global) {
    return undefined;
  }
  return target.mesh === "path" ? global.mean : global.current;
}

/**
 * Accumulates every entity's absolute pose from its ancestors' local ones.
 *
 * Runs after `updatePositions` and `updateRotations` (which evaluate each
 * entity's *local* transform at the current time) and before `updateCamera`
 * (which follows an entity's *absolute* position). See
 * `components/globalPosition.ts` for why this is composed here in float64
 * instead of by Babylon's parenting.
 *
 * Composition is `W(n) = W(parent) · L(n)`, which with scale out of the picture
 * is exactly two lines: a child's offset is rotated by its parent's accumulated
 * orientation before being added, and its own rotation multiplies onto that
 * orientation afterwards. That ordering is what makes a joint work — turn a
 * shoulder and the whole arm below it swings, rather than the shoulder spinning
 * in place while its children hang where they were.
 *
 * `L(n)`'s translation is the declared offset plus any prismatic joint travel
 * `poseJoints` wrote for this entity; its rotation is whatever that same system
 * left on the `Rotation` component. Both are already resolved by the time this
 * runs, so the composition below does not distinguish a driven joint from a
 * topic-bound one.
 *
 * Relies on `sceneConfig.entityOrder` being topological, so a parent's global is
 * already resolved by the time its children are reached. `updateGlobalPosition`
 * maintains that order.
 *
 * Writes in place into each component's own pre-allocated vector and quaternion.
 * Two reasons: this runs every frame and should not allocate, and
 * `setPositionsToTime` assigns `position.current.vector` **by reference** from
 * `cache.allPoints` on an exact sample hit — so accumulating into that vector
 * would corrupt the cache. The rotated offset therefore goes out into a scratch
 * vector rather than being rotated in place.
 */
export function resolveGlobalPositions(state: BabylonState) {
  for (const id of state.sceneConfig.entityOrder ?? []) {
    const entity = state.entities[id];
    const global = entity?.globalPosition;
    if (!global) {
      continue;
    }

    // A root, a dangling parent reference, and a parent whose component has not
    // been created all resolve the same way: the local transform is absolute.
    const parent =
      global.spec === undefined
        ? undefined
        : state.entities[global.spec]?.globalPosition;
    const parentRotation = parent?.rotation ?? IDENTITY;

    global.current.copyFrom(parent?.current ?? babylon.Vector3.ZeroReadOnly);
    if (entity.position) {
      // Placed by the *parent's* orientation, not this entity's own: an offset
      // describes where this entity sits within its parent's frame.
      entity.position.current.vector.applyRotationQuaternionToRef(
        parentRotation,
        SCRATCH_OFFSET,
      );
      global.current.addInPlace(SCRATCH_OFFSET);
    }
    if (entity.jointPose?.motion === "slide") {
      // A prismatic joint's travel, written by `poseJoints`. Added to the
      // declared offset rather than replacing it — the link's rest position is
      // where its travel is measured from — and placed by the same parent
      // orientation, because the axis it slides along is expressed in the same
      // frame the offset is. Summed as a second term rather than added to the
      // local vector first, because that vector is handed out by reference from
      // the position cache and must not be written to.
      entity.jointPose.displacement.applyRotationQuaternionToRef(
        parentRotation,
        SCRATCH_OFFSET,
      );
      global.current.addInPlace(SCRATCH_OFFSET);
    }

    global.rotation.copyFrom(parentRotation);
    if (entity.rotation) {
      global.rotation.multiplyInPlace(entity.rotation.current.quaternion);
    }
  }
}
